const JWT_PATTERN = /eyJ[\w-]+\.[\w-]+\.[\w-]+/g;
const LONG_OPAQUE_PATTERN = /[A-Za-z0-9_-]{40,}/g;
const FRAME_MAX_LENGTH = 10_000;

/** Removes anything token-shaped from text that is about to be sent to analytics. */
export const redactSensitive = (text: string) =>
  text
    .replace(JWT_PATTERN, '[redacted]')
    .replace(LONG_OPAQUE_PATTERN, '[redacted]');

const FRAME_ERROR_MESSAGE_MAX_LENGTH = 200;
const CONNECTION_ERROR_FRAME = 'connection.error';

export interface FrameSummary {
  type?: string;
  errorCode?: number;
  errorStatus?: number;
  errorMessage?: string;
}

/**
 * Keeps the `type` of a websocket frame and, for the server's connection error frame only, its error
 * code, status and message, which say why the server refused the connection. The rest of the
 * payload is never kept.
 */
export const summarizeFrame = (data: unknown): FrameSummary => {
  if (typeof data !== 'string' || data.length > FRAME_MAX_LENGTH) {
    return {};
  }
  try {
    const { type, error } = JSON.parse(data) as {
      type?: unknown;
      error?: { code?: unknown; StatusCode?: unknown; message?: unknown };
    };
    if (typeof type !== 'string') {
      return {};
    }
    if (type !== CONNECTION_ERROR_FRAME || typeof error !== 'object') {
      return { type: type.slice(0, 40) };
    }
    return {
      type: type.slice(0, 40),
      errorCode: typeof error?.code === 'number' ? error.code : undefined,
      errorStatus:
        typeof error?.StatusCode === 'number' ? error.StatusCode : undefined,
      errorMessage:
        typeof error?.message === 'string'
          ? redactSensitive(error.message).slice(
              0,
              FRAME_ERROR_MESSAGE_MAX_LENGTH,
            )
          : undefined,
    };
  } catch {
    return {};
  }
};

/** Reads only the `type` of a websocket frame. */
export const frameTypeOf = (data: unknown): string | undefined =>
  summarizeFrame(data).type;
